import React, {useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCopy, FiCheck, FiRefreshCw, FiAlertTriangle} = FiIcons;

const PersonalizedOpener = ({website}) => {
  const [copied, setCopied] = useState(false);

  // Handle copy to clipboard
  const handleCopy = () => {
    if (website?.personalizedOpener && !website.personalizedOpener.startsWith('[')) {
      navigator.clipboard.writeText(website.personalizedOpener);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!website) {
    return (
      <div className="personalized-opener empty-opener">
        <div className="opener-placeholder">
          <p>Select a website to view its personalized opener</p>
        </div>
      </div>
    );
  }
  
  // Check if there's an error or API configuration issue
  const hasError = website.personalizedOpener && website.personalizedOpener.startsWith('[');
  const errorMessage = hasError ? website.personalizedOpener.replace(/[[\]]/g, '') : null;

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      className="personalized-opener"
    >
      <div className="opener-header">
        <h3>Personalized Opener</h3>
        <div className="opener-actions">
          {!hasError && website.personalizedOpener && (
            <button 
              className="copy-button" 
              onClick={handleCopy} 
              title="Copy to clipboard"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              <span>{copied ? "Copied!" : "Copy"}</span>
            </button>
          )}
        </div>
      </div>
      
      <div className="opener-content">
        {hasError ? (
          <div className="error-message">
            <SafeIcon icon={FiAlertTriangle} className="error-icon" />
            <p>{errorMessage}</p>
          </div>
        ) : website.personalizedOpener ? (
          <p className="opener-text">"{website.personalizedOpener}"</p>
        ) : (
          <p className="no-opener">No personalized opener available. Generate one using the Openers tab.</p>
        )}
      </div>
      
      <div className="opener-footer">
        <div className="website-info">
          <span className="website-name">{website.company || 'Unknown'}</span>
          <span className="website-url">{website.url}</span>
        </div>
        <div className="fit-score">
          <span className={`score-badge score-${website.fitScore >= 7 ? 'high' : website.fitScore >= 5 ? 'medium' : 'low'}`}>
            {website.fitScore}
          </span>
        </div>
      </div>
      
      {/* Qualification Information */}
      {website.qualificationSuccess && website.qualificationReasoning && (
        <div className="qualification-section">
          <div className="qualification-header">
            <h4>Qualification Analysis</h4>
            {website.confidence && (
              <span className="confidence-badge">
                {(() => {
          const confidenceMap = {
            'high': 90,
            'medium': 70,
            'low': 50
          };
          const numericConfidence = typeof website.confidence === 'number' 
            ? website.confidence * 100 
            : confidenceMap[website.confidence] || 70;
          return Math.round(numericConfidence);
        })()}% confidence
              </span>
            )}
          </div>
          
          <div className="qualification-content">
            <p className="qualification-reasoning">{website.qualificationReasoning}</p>
            
            {website.keyIndicators && website.keyIndicators.length > 0 && (
              <div className="indicators-section">
                <h5>Key Indicators</h5>
                <ul className="indicators-list">
                  {website.keyIndicators.map((indicator, index) => (
                    <li key={index} className="indicator-item positive">{indicator}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {website.concerns && website.concerns.length > 0 && (
              <div className="concerns-section">
                <h5>Potential Concerns</h5>
                <ul className="concerns-list">
                  {website.concerns.map((concern, index) => (
                    <li key={index} className="concern-item">{concern}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .personalized-opener {
          background-color: white;
          border-radius: var(--small-radius);
          padding: 1.25rem;
          box-shadow: var(--shadow-sm);
          margin-bottom: 1.5rem;
        }
        
        .empty-opener {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 180px;
          background-color: #f9fafb;
        }
        
        .opener-placeholder {
          color: var(--text-light);
          text-align: center;
        }
        
        .opener-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .opener-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }
        
        .opener-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .copy-button {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
          background-color: #f3f4f6;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .copy-button:hover {
          background-color: #e5e7eb;
        }
        
        .opener-content {
          margin-bottom: 1rem;
        }
        
        .opener-text {
          font-size: 0.875rem;
          line-height: 1.5;
          color: var(--text-color);
          font-style: italic;
        }
        
        .no-opener {
          font-size: 0.875rem;
          line-height: 1.5;
          color: var(--text-light);
          font-style: italic;
        }
        
        .error-message {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          background-color: #fee2e2;
          padding: 1rem;
          border-radius: 8px;
          border-left: 3px solid #ef4444;
        }
        
        .error-message .error-icon {
          color: #b91c1c;
          font-size: 1rem;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .error-message p {
          font-size: 0.875rem;
          line-height: 1.5;
          color: #b91c1c;
          margin: 0;
        }
        
        .opener-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 0.75rem;
          border-top: 1px solid #f3f4f6;
        }
        
        .website-info {
          display: flex;
          flex-direction: column;
        }
        
        .website-name {
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .website-url {
          font-size: 0.75rem;
          color: var(--text-light);
        }
        
        .fit-score {
          display: flex;
          align-items: center;
        }
        
        .score-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          font-weight: 600;
        }
        
        .score-high {
          background-color: #ecfdf5;
          color: #065f46;
        }
        
        .score-medium {
          background-color: #fff7ed;
          color: #9a3412;
        }
        
        .score-low {
          background-color: #f3f4f6;
          color: #4b5563;
        }
        
        .qualification-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #f3f4f6;
        }
        
        .qualification-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }
        
        .qualification-header h4 {
          font-size: 0.875rem;
          font-weight: 600;
          margin: 0;
          color: var(--text-color);
        }
        
        .confidence-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          background-color: #eff6ff;
          color: #1d4ed8;
          border-radius: 12px;
          font-weight: 500;
        }
        
        .qualification-content {
          font-size: 0.875rem;
          line-height: 1.5;
        }
        
        .qualification-reasoning {
          color: var(--text-color);
          margin-bottom: 1rem;
          font-style: italic;
        }
        
        .indicators-section,
        .concerns-section {
          margin-bottom: 0.75rem;
        }
        
        .indicators-section h5,
        .concerns-section h5 {
          font-size: 0.75rem;
          font-weight: 600;
          margin: 0 0 0.5rem 0;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .indicators-section h5 {
          color: #065f46;
        }
        
        .concerns-section h5 {
          color: #9a3412;
        }
        
        .indicators-list,
        .concerns-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .indicator-item,
        .concern-item {
          font-size: 0.75rem;
          padding: 0.375rem 0.75rem;
          margin-bottom: 0.25rem;
          border-radius: 6px;
          line-height: 1.4;
        }
        
        .indicator-item {
          background-color: #ecfdf5;
          color: #065f46;
          border-left: 3px solid #10b981;
        }
        
        .concern-item {
          background-color: #fff7ed;
          color: #9a3412;
          border-left: 3px solid #f59e0b;
        }
      `}</style>
    </motion.div>
  );
};

export default PersonalizedOpener;